/**
 * @fileoverview Custom OpenLayers control for toggling result layer visibility.
 * @module map/OverlayControl
 */

import { OVERLAYCONTROL_LAYER_NAMES } from '../constants.js';
import { DropdownControl } from './DropdownControl.js';



/**
 * Custom OpenLayers control that provides a dropdown menu for toggling result layer visibility.
 * Displays a stack icon button that reveals a dropdown with checkbox options on click.
 * @class
 * @extends DropdownControl
 */
export class OverlayControl extends DropdownControl {
    /**
     * Creates a new OverlayControl instance.
     * @param {Object} [options={}] - Control options
     * @param {Object} [options.visibilityState={}] - Initial visibility state object keyed by layer id
     * @param {Function} [options.onVisibilityChange] - Callback invoked when visibility changes: (layerId, visible, allState) => void
     * @param {HTMLElement} [options.target] - Target element to render the control into
     */
    constructor(options = {}) {
        super({
            //buttonIconHtml: '<i class="bi bi-stack"></i>',
            buttonIconHtml: '<i class="bi bi-layers-half"></i>',
            buttonClass: 'ol-overlay-control-button',
            buttonTitle: 'Result overlays',
            elementClass: 'ol-overlay-control',
            dropdownClass: 'overlay-control-dropdown',
            target: options.target
        });

        this.onVisibilityChange = options.onVisibilityChange || (() => {});

        // Initialize visibility state from options or default all to false
        this.visibilityState = {};
        for (const layer of OVERLAYCONTROL_LAYER_NAMES) {
            this.visibilityState[layer.id] = options.visibilityState?.[layer.id] ?? false;
        }

        // Populate dropdown with checkbox options
        this.buildDropdown();
    }

    /**
     * Builds the dropdown menu with checkboxes for each layer.
     * @private
     */
    buildDropdown() {
        this.dropdown.innerHTML = '';

        // Create title bar at the top
        const titleBar = document.createElement('div');
        titleBar.className = 'overlay-control-title';
        titleBar.textContent = 'Always overlay results from these tabs:';
        this.dropdown.appendChild(titleBar);

        // Create "Toggle All" checkbox at the top
        const toggleAllItem = document.createElement('div');
        toggleAllItem.className = 'overlay-control-item overlay-control-toggle-all';

        const toggleAllCheckbox = document.createElement('input');
        toggleAllCheckbox.type = 'checkbox';
        toggleAllCheckbox.id = 'overlay-toggle-all';
        toggleAllCheckbox.className = 'overlay-control-checkbox';
        toggleAllCheckbox.checked = this.areAllVisible();

        const toggleAllLabel = document.createElement('label');
        toggleAllLabel.htmlFor = 'overlay-toggle-all';
        toggleAllLabel.textContent = 'Toggle All';
        toggleAllLabel.className = 'overlay-control-label';

        toggleAllItem.appendChild(toggleAllCheckbox);
        toggleAllItem.appendChild(toggleAllLabel);
        this.dropdown.appendChild(toggleAllItem);

        // Store reference for updating
        this.toggleAllCheckbox = toggleAllCheckbox;

        // Handle toggle all click
        toggleAllCheckbox.addEventListener('change', (e) => {
            e.stopPropagation();
            this.toggleAll(toggleAllCheckbox.checked);
        });

        // Create separator
        const separator = document.createElement('div');
        separator.className = 'overlay-control-separator';
        this.dropdown.appendChild(separator);

        // Create checkbox for each layer
        this.layerCheckboxes = {};
        for (const layer of OVERLAYCONTROL_LAYER_NAMES) {
            const item = document.createElement('div');
            item.className = 'overlay-control-item';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = `overlay-${layer.id}`;
            checkbox.className = 'overlay-control-checkbox';
            checkbox.checked = this.visibilityState[layer.id];

            const label = document.createElement('label');
            label.htmlFor = `overlay-${layer.id}`;
            label.textContent = layer.name;
            label.className = 'overlay-control-label';

            item.appendChild(checkbox);
            item.appendChild(label);
            this.dropdown.appendChild(item);

            // Store reference
            this.layerCheckboxes[layer.id] = checkbox;

            // Handle checkbox change
            checkbox.addEventListener('change', (e) => {
                e.stopPropagation();
                this.setLayerVisibility(layer.id, checkbox.checked);
            });
        }
    }

    /**
     * Checks if all layers are currently visible.
     * @returns {boolean} True if all layers are visible
     */
    areAllVisible() {
        for (const layer of OVERLAYCONTROL_LAYER_NAMES) {
            if (!this.visibilityState[layer.id]) {
                return false;
            }
        }
        return true;
    }

    /**
     * Updates the "Toggle All" checkbox state based on individual checkbox states.
     * @private
     */
    updateToggleAllState() {
        if (this.toggleAllCheckbox) {
            this.toggleAllCheckbox.checked = this.areAllVisible();
        }
    }

    /**
     * Toggles all layer visibilities on or off.
     * @param {boolean} visible - Whether all layers should be visible
     */
    toggleAll(visible) {
        for (const layer of OVERLAYCONTROL_LAYER_NAMES) {
            this.visibilityState[layer.id] = visible;
            if (this.layerCheckboxes[layer.id]) {
                this.layerCheckboxes[layer.id].checked = visible;
            }
        }
        this.onVisibilityChange(null, visible, { ...this.visibilityState });
    }

    /**
     * Sets the visibility of a specific layer.
     * @param {string} layerId - The layer identifier
     * @param {boolean} visible - Whether the layer should be visible
     */
    setLayerVisibility(layerId, visible) {
        this.visibilityState[layerId] = visible;
        this.updateToggleAllState();
        this.onVisibilityChange(layerId, visible, { ...this.visibilityState });
    }

    /**
     * Gets the current visibility state of all layers.
     * @returns {Object} Object with layer ids as keys and visibility booleans as values
     */
    getVisibilityState() {
        return { ...this.visibilityState };
    }
}
